import { useState, useEffect, useRef, useCallback } from 'react';
import { getCustomer } from '../services/api/customerApi';

/**
 * Custom hook for fetching and managing single customer detail data
 * @param {string} customerId - The ID of the customer to fetch
 * @returns {Object} Customer detail state and controls
 */
export const useCustomerDetail = (customerId) => {
  const [customer, setCustomer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Use ref to prevent duplicate API calls in StrictMode
  const hasFetched = useRef(false);
  const currentCustomerId = useRef(customerId);

  useEffect(() => {
    // Reset when customerId changes
    if (currentCustomerId.current !== customerId) {
      currentCustomerId.current = customerId;
      hasFetched.current = false;
      setCustomer(null);
    }

    if (!hasFetched.current && customerId) {
      hasFetched.current = true;
      fetchCustomer();
    }
  }, [customerId]);

  const fetchCustomer = async () => {
    if (!customerId) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const response = await getCustomer(customerId);
      setCustomer(response.data);
    } catch (err) {
      // Handle 404 errors gracefully
      if (err.response?.status === 404) {
        setError('Customer not found');
      } else {
        setError(err.response?.data?.message || 'Failed to fetch customer details');
      }
      console.error('Error fetching customer:', err);
    } finally {
      setLoading(false);
    }
  };

  const refreshCustomer = useCallback(async () => {
    hasFetched.current = false;
    await fetchCustomer();
  }, [customerId]);

  return {
    customer,
    loading,
    error,
    refreshCustomer
  };
};
